<template>
  <div>
    <b-modal
      id="add_a_user"
      modal-class="modal-add-user"
      header-bg-variant="white"
      centered
      header-class="d-flex align-items-center"
      hide-footer
      hide-header-close
      size="md"
      @close="onClickCancel"
      @hide="trackHideModal"
    >
      <button
        class="close-icon"
        @click="$bvModal.hide('add_a_user')"
      >
        <close-icon />
      </button>
      <div class="modal-add-user-body mb-2">
        <div class="modal-add-user-header">
          Add new retailer
        </div>
        <div class="sub-title">
          {{
            isFormDisabled
              ? 'This retailer exists in our platform. Please choose from the dropdown'
              : 'Complete all fields'
          }}
        </div>
        <div class="mt-2">
          <validation-observer
            v-if="!isFormDisabled"
            ref="addRetailerForm"
            #default="{ invalid }"
          >
            <b-form
              class="mt-2"
              @submit.prevent="onSubmit(false)"
            >
              <div>
                <validation-provider
                  #default="{ errors }"
                  name="Store Name"
                  rules="required|min:3"
                >
                  <b-form-group
                    label="Store Name"
                    label-class="pb-0"
                  >
                    <b-form-input
                      v-model="form.entityName"
                      placeholder="Store Name"
                      :state="errors.length > 0 ? false : null"
                      :readonly="isFormReadonly"
                      @blur="trimField('entityName')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
                <validation-provider
                  #default="{ errors }"
                  name="First Name"
                  rules="required"
                >
                  <b-form-group
                    label="First Name"
                    label-class="pb-0"
                  >
                    <b-form-input
                      v-model="form.firstName"
                      placeholder="First Name"
                      :state="errors.length > 0 ? false : null"
                      :readonly="isFormReadonly"
                      @blur="trimField('firstName')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
                <validation-provider
                  #default="{ errors }"
                  name="Last Name"
                  rules="required|min:1"
                >
                  <b-form-group
                    label="Last Name"
                    label-class="pb-0"
                  >
                    <b-form-input
                      v-model="form.lastName"
                      placeholder="Last Name"
                      :state="errors.length > 0 ? false : null"
                      :readonly="isFormReadonly"
                      @blur="trimField('lastName')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
                <validation-provider
                  #default="{ errors }"
                  name="Email"
                  rules="required|email"
                >
                  <b-form-group
                    label="Email"
                    label-class="pb-0"
                  >
                    <b-form-input
                      v-model="form.email"
                      placeholder="Email"
                      :state="errors.length > 0 ? false : null"
                      :readonly="isFormReadonly"
                      @blur="trimField('email')"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
                <validation-provider>
                  <b-form-group
                    label="Whatsapp Number"
                    label-for="account-phone"
                  >
                    <vue-tel-input
                      v-model="phoneNumber"
                      :input-options="inputOptions"
                      mode="international"
                      name="phone"
                      :class="isFormReadonly ? 'tel-input-disabled' : ''"
                      :disabled="isFormReadonly"
                      :auto-default-country="autoDefaultCountry"
                      :default-country="defaultCountry"
                      @input="onInputPhoneNumber"
                    />
                    <small
                      v-if="isNotValidPhoneNumber"
                      class="text-danger"
                    >Please enter the valid phone number</small>
                  </b-form-group>
                </validation-provider>
                <template v-if="countries.length">
                  <validation-provider
                    #default="{ errors }"
                    name="Country"
                    rules="required"
                  >
                    <b-form-group
                      label="Country"
                      label-class="pb-0"
                    >
                      <v-select
                        v-model="form.country"
                        :options="countries"
                        placeholder="Country"
                        :disabled="isFormReadonly"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </template>
              </div>
              <div class="w-100 d-flex justify-content-end mt-2 mb-1">
                <b-button
                  variant="outline-primary"
                  :disabled="isCreating"
                  @click="onClickCancel"
                >
                  Cancel
                </b-button>
                <b-button
                  class="ml-1"
                  type="submit"
                  variant="custom-primary"
                  :disabled="invalid || isNotValidPhoneNumber || isCreating"
                >
                  <span> Add retailer </span>
                  <feather-icon
                    v-if="isCreating"
                    class="ml-1"
                    icon="LoaderIcon"
                  />
                </b-button>
              </div>
            </b-form>
          </validation-observer>
          <div v-if="isFormDisabled">
            <b-form-group
              label-for="exclude-countries"
              label="Exclude Countries"
            >
              <template #label>
                <div class="d-flex justify-content-between">
                  <span class="d-flex">
                    Select Retailers
                  </span>
                  <span class="badge badge-pill badge-light-primary elements-center">
                    {{ retailers.length }} Retailer{{ retailers.length > 1 ? 's' : '' }} selected
                  </span>
                </div>
              </template>
              <b-dropdown
                v-click-outside="hideCountriesDropdown"
                block
                variant="none"
                class="countries-dropdown"
                :class="{ show: showRetailers }"
                :menu-class="`w-100 retailers-menu scroll-bar ${showRetailers ? 'show' : ''}`"
                toggle-class="p-0"
                toggle-tag="div"
                dropdown
                no-caret
                @show.prevent
                @hide.prevent
              >
                <template #button-content>
                  <v-select
                    v-model="retailers"
                    :options="allRetailers"
                    multiple
                    :close-on-select="false"
                    :deselect-from-dropdown="true"
                    :no-drop="true"
                    class="restricted-countries-dropdown"
                    placeholder="Select Retailers"
                    label="entityName"
                    @search:focus="showRetailers = true"
                    @search="searchRetailers"
                  />
                </template>
                <b-dropdown-item
                  v-for="retailer in allRetailersLocal"
                  :key="`ac_${retailer._id}`"
                  :active="retailers.map((_) => _._id).includes(retailer._id)"
                  :disabled="retailer.isAdded"
                  @click="handleSelectRetailerLocal(retailer)"
                >
                  {{ getRetailerStatus(retailer) }} {{ retailer.entityName }}
                </b-dropdown-item>
              </b-dropdown>
            </b-form-group>
            <div class="w-100 d-flex justify-content-end mt-2 mb-1">
              <b-button
                variant="outline-primary"
                :disabled="isCreating"
                @click="onClickCancel"
              >
                Cancel
              </b-button>
              <b-button
                class="ml-1"
                type="submit"
                variant="custom-primary"
                :disabled="!retailers.length || isCreating"
                @click="onSubmit(true)"
              >
                <span> Add retailer </span>
                <feather-icon
                  v-if="isCreating"
                  class="ml-1"
                  icon="LoaderIcon"
                />
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import { apiToastError } from '@/@core/utils/toast'
import { email, min, required } from '@validations'
import CloseIcon from '@/@core/assets/svg-components/CloseIcon.vue'
import {
  BButton,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormGroup,
  BFormInput,
  BModal,
  VBModal,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import countryCodeData from '@/assets/data/countryCode.json'
import { useAddUser } from '@/composables/useAddUser'

export default {
  name: 'AddUserModal',
  components: {
    BModal,
    BButton,
    BFormGroup,
    BFormInput,
    BForm,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    FeatherIcon,
    BDropdown,
    BDropdownItem,
    CloseIcon,
  },
  directives: {
    'b-modal': VBModal,
  },
  setup() {
    return { ...useAddUser() }
  },
  data() {
    return {
      required,
      email,
      min,
      phoneNumber: '',
      autoDefaultCountry: true,
      defaultCountry: '',
      showRetailers: false,
      isValidPhoneNumber: false,
      inputOptions: {
        id: 'whatsapp_number',
        placeholder: 'Enter whatsapp number',
      },
    }
  },
  computed: {
    isNotValidPhoneNumber() {
      return this.form?.phone && !this.isValidPhoneNumber
    },
  },
  created() {
    this.loadCountries()
  },
  emits: ['load-connections'],
  methods: {
    onInputPhoneNumber(number, phoneObject) {
      this.form.phone = number && phoneObject.nationalNumber
      this.form.countryCode = `+${phoneObject.countryCallingCode}`
      this.isValidPhoneNumber = !!phoneObject.valid
    },
    openModal(leadDetails) {
      this.updateFormData(leadDetails)
      this.form.country = this.matchCountry(leadDetails.country) || ''
      if (this.form.country !== '') {
        this.updateCountryCode(this.form.country)
      }
      this.$bvModal.show('add_a_user')
      this.fromLeadAI = true
      this.isFormReadonly = leadDetails?.isFormReadonly ?? true
      this.setPhoneData(leadDetails)
    },
    trackHideModal() {
      this.$emit('on-add-retailer-close', {
        ...this.getPayloadData(),
        leadId: this.form.leadId,
      })
      this.resetForm()
    },
    setPhoneData(leadDetails) {
      this.phoneNumber = leadDetails?.phoneNumber || ''

      if (leadDetails?.phoneObject) {
        this.onInputPhoneNumber(leadDetails.phoneNumber, leadDetails.phoneObject)
      }
    },
    updateCountryCode(countryName) {
      const countryData = countryCodeData.find(country => country.name === countryName)

      if (countryData) {
        this.autoDefaultCountry = false
        this.defaultCountry = countryData.code
      }
    },
    async onSubmit(shouldProceed = false) {
      const eventName = shouldProceed ? 'on-add-retailer-exist-proceed' : 'on-add-retailer'
      this.$emit(eventName, {
        ...this.getPayloadData(),
        leadId: this.form.leadId,
      })
      const result = await this.submitForm(shouldProceed)
      if (result.success) {
        this.handleSuccess(result.payload, shouldProceed)
      } else {
        this.handleError(result.payload, result.error, shouldProceed, result.is422)
      }
    },
    handleSuccess(payload, shouldProceed) {
      const eventName = shouldProceed ? 'on-add-existing-retailer-success' : 'on-add-retailer-success'
      this.$emit(eventName, {
        ...payload,
        leadId: this.form.leadId,
      })
      this.onClickCancel()
      this.$emit('load-connections')
    },
    sendFallbackToast(err) {
      if (!this.$listeners?.['on-add-existing-retailer-failed'] && !this.$listeners?.['on-add-retailer-failed']) {
        apiToastError(err, 'TimesIcon')
      }
    },
    handleErrorEvents(payload, err, shouldProceed) {
      let eventName = shouldProceed ? 'on-add-existing-retailer-failed' : 'on-add-retailer-failed'
      if (err?.response?.status === 422) {
        eventName = 'on-add-retailer-exists'
      }
      this.$emit(eventName, {
        ...payload,
        leadId: this.form.leadId,
      }, err)

      this.sendFallbackToast(err)
    },
    handleError(payload, err, shouldProceed, is422) {
      this.handleErrorEvents(payload, err, shouldProceed)
      if (!is422) {
        // non-422 errors: toast was not shown by composable for old view compatibility
      }
    },
    onClickCancel() {
      this.$emit('on-add-retailer-close', {
        ...this.getPayloadData(),
        leadId: this.form.leadId,
      })
      this.resetForm()
      this.$bvModal.hide('add_a_user')
    },
    hideCountriesDropdown() {
      this.showRetailers = false
    },
    newRetailers() {
      return this.allRetailers.find(retailer => !retailer.isAdded)
    },
    handleSelectRetailerLocal(retailer = '') {
      this.handleSelectRetailer(retailer)
      if (retailer && !retailer.isAdded) {
        this.$emit('on-select-existing-retailer', {
          ...this.getPayloadData(),
          leadId: this.form.leadId,
          selectedRetailer: retailer,
        })
      }
      this.showRetailers = false
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include'; // Bootstrap includes
@import '~@core/scss/base/components/include';

.close-icon {
  position: absolute;
  height: 35px;
  width: 35px;
  background-color: white;
  top: -36px;
  right: -15px;
  border: none;
  border-radius: 5px;
  box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
  transition: all 0.23s ease 0.1s;
  &:hover {
    transform: translate(-3px, 3px);
  }
}

.vs__search {
  &::placeholder {
    color: $text-muted !important;
  }
}

.retailers-menu {
  max-height: 320px;
  overflow: auto;
}

.vue-tel-input {
  height: 2.714rem;
  border: 1px solid #E0E0E0;

  .vti__dropdown-list {
    z-index: 200;
  }
}

.tel-input-disabled > * {
  cursor: not-allowed !important;
}

.tel-input-disabled > div.vti__dropdown:hover {
  background-color: unset !important;
}
</style>
